import type { BadgeTone } from '@chopchop-se/ui';
import type {
  PurchaseOrderLineView,
  PurchaseOrderStatus,
  ReceiptStatus,
  ReceivingScanResult,
} from '@warehouse/shared';

/**
 * Pure presentation logic for the receiving screens. Kept out of the components
 * so the rules that actually matter on the floor — which PO line a scan should
 * pre-select, how much is still expected, whether a scan can be received at all
 * — are unit-testable without rendering anything.
 */

export const PO_STATUS_TONE: Record<PurchaseOrderStatus, BadgeTone> = {
  open: 'info',
  partially_received: 'warning',
  received: 'success',
  closed: 'neutral',
  cancelled: 'danger',
};

export const RECEIPT_STATUS_TONE: Record<ReceiptStatus, BadgeTone> = {
  open: 'warning',
  closed: 'success',
};

/** Decimal-string subtraction, floored at zero — never a float. */
export function remainingQty(line: Pick<PurchaseOrderLineView, 'qtyBase' | 'receivedQtyBase'>): number {
  const remaining = Number(line.qtyBase) - Number(line.receivedQtyBase);
  return remaining > 0 ? remaining : 0;
}

/**
 * Which PO line a scan should pre-select.
 *
 * "The right order and item should automatically appear, filtered on the right
 * order" (spec §5.7). With one open line for the article the choice is obvious;
 * with several, the one with the most still outstanding is the best guess, and
 * the operator can still switch. With none open we return the first line for
 * the article (an over-receipt is a real event, not a reason to hide the line).
 */
export function pickPoLine(
  lines: readonly PurchaseOrderLineView[],
  articleId: string | null | undefined,
): PurchaseOrderLineView | null {
  if (!articleId) return null;
  const forArticle = lines.filter((line) => line.articleId === articleId);
  if (forArticle.length === 0) return null;

  const open = forArticle.filter((line) => remainingQty(line) > 0);
  const pool = open.length > 0 ? open : forArticle;
  return [...pool].sort((a, b) => remainingQty(b) - remainingQty(a))[0] ?? null;
}

/**
 * The quantity to pre-fill after a scan.
 *
 * Priority: what the LABEL said (AI 37 count / AI 310n weight) beats the
 * barcode's configured `qtyPerScan`, because a variable-measure case knows its
 * own contents. Neither is authoritative — the operator confirms.
 */
export function suggestedQty(scan: ReceivingScanResult | null): string {
  if (!scan) return '';
  if (scan.quantity && Number(scan.quantity) > 0) return trimQty(scan.quantity);
  if (scan.article) return trimQty(scan.article.qtyPerScan);
  return '';
}

/** The unit a scanned quantity is expressed in. */
export function suggestedUnitId(scan: ReceivingScanResult | null): string | null {
  if (!scan?.article) return null;
  // A weight from the label is in kg, which is a base-unit quantity for a
  // catch-weight article — the barcode's packaging unit does not apply to it.
  if (scan.quantityUnit === 'kg') return scan.article.baseUnitId;
  return scan.article.unitId ?? scan.article.baseUnitId;
}

/** `12.000` → `12`, `0.500` → `0.5`. Input for a number field, not display. */
export function trimQty(value: string): string {
  if (!/^-?\d+(\.\d+)?$/.test(value)) return value;
  return value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value;
}

export type ScanAction =
  | { kind: 'receive'; articleId: string }
  | { kind: 'pallet'; palletId: string }
  | { kind: 'sscc'; sscc: string }
  | { kind: 'bin'; binId: string }
  | { kind: 'teach'; code: string }
  | { kind: 'ambiguous' }
  | { kind: 'none' };

/**
 * What the receiving screen should DO with a scan. One place, so the session
 * screen, the put-away screen and the teach screen cannot disagree about what
 * "this scan" meant.
 */
export function scanAction(scan: ReceivingScanResult | null): ScanAction {
  if (!scan) return { kind: 'none' };
  if (scan.article) return { kind: 'receive', articleId: scan.article.articleId };
  if (scan.pallet) return { kind: 'pallet', palletId: scan.pallet.palletId };
  if (scan.ssccUnknown && scan.sscc) return { kind: 'sscc', sscc: scan.sscc };
  if (scan.bin) return { kind: 'bin', binId: scan.bin.binId };
  if (scan.ambiguousArticleIds.length > 0) return { kind: 'ambiguous' };
  if (scan.needsTeach) return { kind: 'teach', code: scan.data };
  return { kind: 'none' };
}

/** Progress bar width for a PO line, 0-100. */
export function linePct(line: Pick<PurchaseOrderLineView, 'qtyBase' | 'receivedQtyBase'>): number {
  const ordered = Number(line.qtyBase);
  if (!Number.isFinite(ordered) || ordered <= 0) return 0;
  const pct = (Number(line.receivedQtyBase) / ordered) * 100;
  return Math.max(0, Math.min(100, Math.round(pct)));
}
